import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Heart } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useProfile } from '@/lib/providers';
import Loading from '@/components/loading';
import { LOGIN_PATH } from '@/lib/constants';

type PeekResult = {
  samen_name: string;
  invited_email: string;
  status: string;
  expires_at: string;
};

/**
 * Landing page for the join link in a samen invite mail.
 *
 * Public route on purpose: the invitee may well arrive signed out, and
 * ProtectedLayout would strand them on a login page with the token gone from
 * the URL. Accepting still requires a session -- accept_samen_invite() checks
 * the signed-in address against the one the invite was sent to.
 */
export default function SamenUitnodigingPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { profile, loadingProfile, refreshProfile } = useProfile();

  const [checking, setChecking] = useState(true);
  const [invite, setInvite] = useState<PeekResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (loadingProfile) return;
    if (!token) {
      setError('Deze link is niet compleet.');
      setChecking(false);
      return;
    }
    // Signed out: nothing to look up yet, peek_samen_invite is for
    // authenticated callers only. Show the sign-in prompt instead.
    if (!profile) {
      setChecking(false);
      return;
    }

    let cancelled = false;
    (async () => {
      const { data, error: peekError } = await supabase.rpc(
        'peek_samen_invite',
        { p_token: token },
      );
      if (cancelled) return;

      const row = (data as PeekResult[] | null)?.[0] ?? null;
      if (peekError || !row) {
        setError('Deze uitnodiging bestaat niet of is niet meer geldig.');
      } else if (row.status === 'revoked') {
        setError('Deze uitnodiging is ingetrokken.');
      } else if (new Date(row.expires_at) < new Date()) {
        setError('Deze uitnodiging is verlopen.');
      } else {
        setInvite(row);
      }
      setChecking(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [token, profile?.id, loadingProfile]);

  const handleAccept = async () => {
    if (!token || accepting) return;
    setAccepting(true);
    setError(null);
    try {
      const { error: acceptError } = await supabase.rpc('accept_samen_invite', {
        p_token: token,
      });
      if (acceptError) throw acceptError;

      await refreshProfile();
      navigate('/samen', { replace: true });
    } catch (err) {
      console.error('Failed to accept samen invite:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Er ging iets mis. Probeer opnieuw.',
      );
      setAccepting(false);
    }
  };

  if (loadingProfile || checking) return <Loading />;

  return (
    <div className="min-h-screen p-8 flex items-center justify-center">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 text-center space-y-4">
          <Heart className="h-8 w-8 text-primary mx-auto" />

          {!profile ? (
            <>
              <h1 className="text-xl font-semibold">Je bent uitgenodigd</h1>
              <p className="text-sm text-muted-foreground">
                Log in met het account waarvoor deze uitnodiging bedoeld is, dan
                kun je je bij de Samen groep voegen.
              </p>
              <Button
                className="w-full"
                onClick={() =>
                  navigate(
                    `${LOGIN_PATH}?next=${encodeURIComponent(
                      `/samen/uitnodiging?token=${token ?? ''}`,
                    )}`,
                  )
                }
              >
                Inloggen
              </Button>
            </>
          ) : error ? (
            <>
              <h1 className="text-xl font-semibold">Uitnodiging</h1>
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/')}
              >
                Naar de app
              </Button>
            </>
          ) : invite ? (
            <>
              <h1 className="text-xl font-semibold">
                Je bent uitgenodigd voor {invite.samen_name}
              </h1>
              <p className="text-sm text-muted-foreground">
                Word lid van deze Samen groep om samen te mediteren, sessies te
                plannen en bij te praten.
              </p>
              <Button
                className="w-full"
                onClick={handleAccept}
                disabled={accepting}
              >
                {accepting ? 'Bezig...' : 'Word lid van de groep'}
              </Button>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
